import React, { useState } from 'react';
import EditableQuestionCard from './EditableQuestionCard';
import { Button } from '../ui/BaseComponents';
import { notifyWarning } from '../../services/notificationService';

/**
 * QuestionsTab - Tabbed panel for managing quiz questions
 * Supports drag-and-drop reordering, add/edit/delete
 * 
 * @param {Object} props
 * @param {Array} props.questions - Array of question objects
 * @param {Function} props.onQuestionsChange - Callback when questions array changes
 * @param {Function} props.onAIGenerate - Callback for AI generation
 * @param {Function} props.onImport - Callback for importing questions
 */
function QuestionsTab({
  questions = [],
  onQuestionsChange,
  onAIGenerate,
  onImport
}) {
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleAddQuestion = () => {
    const newQuestion = {
      question: '',
      options: ['', '', '', ''],
      correctAnswerIndices: [0], // STANDARD: Array of correct answer indices
      isNew: true // Flag to open in edit mode automatically
    };
    onQuestionsChange([...questions, newQuestion]);
  };

  const handleUpdateQuestion = (index, updatedQuestion) => {
    const newQuestions = [...questions];
    newQuestions[index] = updatedQuestion;
    onQuestionsChange(newQuestions);
  };

  const handleDeleteQuestion = (index) => {
    if (questions.length <= 1) {
      notifyWarning('Eine Vorlage muss mindestens eine Frage enthalten.');
      return;
    }

    const newQuestions = questions.filter((_, i) => i !== index);
    onQuestionsChange(newQuestions);
  };

  const handleDuplicateQuestion = (index) => {
    const questionToDuplicate = { ...questions[index] };
    const newQuestions = [
      ...questions.slice(0, index + 1),
      questionToDuplicate,
      ...questions.slice(index + 1)
    ];
    onQuestionsChange(newQuestions);
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex === null || draggedIndex === index) return;

    const newQuestions = [...questions];
    const draggedItem = newQuestions[draggedIndex];

    // Remove from old position
    newQuestions.splice(draggedIndex, 1);
    // Insert at new position
    newQuestions.splice(index, 0, draggedItem);

    onQuestionsChange(newQuestions);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Empty State
  if (questions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
        <svg className="w-16 h-16 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Noch keine Fragen</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6 text-center max-w-md">
          Erstelle dein Quiz, indem du Fragen manuell hinzufügst, aus einer Datei importierst oder KI-Generierung verwendest.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Button variant="primary" onClick={handleAddQuestion}>
            <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Frage hinzufügen
          </Button>
          {onAIGenerate && (
            <Button variant="secondary" onClick={onAIGenerate}>
              <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              KI generieren
            </Button>
          )}
          {onImport && (
            <Button variant="secondary" onClick={onImport}>
              📤 JSON importieren
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Question List
  return (
    <div className="space-y-4">
      {/* Action Bar */}
      <div className="flex items-center justify-between mb-4 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/30 dark:to-secondary-900/30 rounded-lg border border-primary-200 dark:border-primary-700">
        <div className="flex items-center gap-2">
          <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <div>
            <h3 className="font-semibold text-gray-800 dark:text-white">
              {questions.length} Frage{questions.length !== 1 ? 'n' : ''}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Ziehen zum Sortieren, Klicken zum Bearbeiten</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="primary" size="sm" onClick={handleAddQuestion}>
            Frage hinzufügen
          </Button>
          {onAIGenerate && (
            <Button variant="secondary" size="sm" onClick={onAIGenerate}>
              KI generieren
            </Button>
          )}
          {onImport && (
            <Button variant="secondary" size="sm" onClick={onImport}>
              📤 Importieren
            </Button>
          )}
        </div>
      </div>

      {/* Question List */}
      <div className="space-y-3">
        {questions.map((question, index) => (
          <div
            key={question.id || `q-${index}-${question.question?.slice(0, 20) || 'empty'}`}
            draggable={true}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            onDragEnter={(e) => e.preventDefault()}
            style={{ cursor: 'move' }}
            className={`transition-all duration-200 ${
              draggedIndex === index 
                ? 'opacity-30 scale-95' 
                : draggedIndex !== null 
                  ? 'opacity-100' 
                  : 'opacity-100'
            }`}
          >
            <EditableQuestionCard
              question={question}
              index={index}
              onUpdate={handleUpdateQuestion}
              onDelete={handleDeleteQuestion}
              onDuplicate={handleDuplicateQuestion}
              dragHandle={true}
            />
          </div>
        ))}
      </div>

      {/* Add Question Button (Bottom) */}
      <Button
        variant="ghost"
        onClick={handleAddQuestion}
        className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/30"
        size="lg"
      >
        <svg className="w-5 h-5 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Weitere Frage hinzufügen
      </Button>
    </div>
  );
}

export default QuestionsTab;
